import { useCallback, useMemo, useState } from "react";
import type { TranslationKeys } from "../../../../../i18n/types";
import type { GeneralFormData } from "../types";
import { type AcceptedRow, isReadOnlyRow, resolveFormKey } from "./airtableImportMerge";
import type { AirtableDiffRow, AirtableRowKind } from "./airtableImportTypes";

/**
 * A row's identity for UI state — accept, expansion, test ids.
 *
 * NOT `fieldKey`. `fieldKey` is the row's DESTINATION, and one destination legitimately produces
 * several rows: the backend returns `applicable_rows + refusal_rows`, and both `overtimeConfig`
 * (Daily + Weekly OT) and `hoursRateConfig` (Evening + Weekend differentials) are merge targets of two
 * intake columns each. If one column resolves and the other is refused — or both are refused — two
 * rows arrive carrying the same `fieldKey`. Keying UI state on it made MUI treat them as one row, so
 * checking either box checked both, and expansion was shared. That silently defeats per-field
 * acceptance, which is the guarantee this whole flow rests on, while still rendering two rows.
 *
 * The position in the server's `rows` array is unique by construction and stable for as long as the
 * state keyed on it lives: the grid never sorts (every column is `sortable: false`), and a fresh diff
 * replaces rows and accept state together through `loadDiff` / `loadDiffAfterRefusal`. The `fieldKey`
 * suffix is there so a test id stays greppable.
 */
export function rowIdFor(row: AirtableDiffRow, index: number): string {
	return `${index}-${row.fieldKey}`;
}

/** One diff row plus everything the grid needs to decide how it renders and whether it can be applied. */
export type AirtableReviewEntry = {
	/** Unique per RENDERED row — see `rowIdFor`. Not the destination field. */
	rowId: string;
	row: AirtableDiffRow;
	/** The `GeneralFormData` key it writes to, or `null` when this form has no such field. */
	formKey: keyof GeneralFormData | null;
	/** Whether a human can accept it at all (read-only kinds and unmappable fields cannot). */
	acceptable: boolean;
	/** Current accept state. Always false for a row that is not `acceptable`. */
	checked: boolean;
	/**
	 * Why a row cannot be accepted, when the reason is ours rather than the server's: the diff names a
	 * field this form does not have, so merging it would put an unknown key into the config payload.
	 */
	unknownField: boolean;
};

export type AirtableReviewState = {
	entries: AirtableReviewEntry[];
	countsByKind: Record<AirtableRowKind, number>;
	/** How many rows a human could accept — the denominator of the review's progress. */
	acceptableCount: number;
	/** How many they have accepted so far — the numerator, and `acceptedRows.length`. */
	acceptedCount: number;
	/** The checked rows, resolved onto their form fields: exactly what the merge will write. */
	acceptedRows: AcceptedRow[];
	/** Non-empty means Apply is disabled; the keys are rendered in its tooltip. */
	applyDisabledReasons: TranslationKeys[];
	/** The diff currently under review. Owned by the hook so rows and accept state can never disagree. */
	rows: AirtableDiffRow[];
	setChecked: (rowId: string, checked: boolean) => void;
	/**
	 * A freshly-previewed diff. Accept state is seeded from each row's `defaultChecked` — deterministic
	 * rows start accepted, model-extracted ones do not.
	 */
	loadDiff: (rows: AirtableDiffRow[]) => void;
	/**
	 * A diff re-read after an Apply was REFUSED because the Airtable record moved. Nothing is
	 * pre-accepted — not even the deterministic rows — because the operator's acceptances were made
	 * against text that has since changed, and carrying any of them over (or quietly re-checking a
	 * different set) is exactly the rubber-stamping the flow exists to prevent.
	 */
	loadDiffAfterRefusal: (rows: AirtableDiffRow[]) => void;
};

const EMPTY_COUNTS: Record<AirtableRowKind, number> = {
	deterministic: 0,
	llm: 0,
	unresolvable: 0,
	out_of_band: 0,
	blocked: 0,
	protected: 0,
};

/** Which rows a fresh diff starts with accepted. A read-only row never does, whatever it says. */
function defaultCheckedKeys(rows: AirtableDiffRow[]): Set<string> {
	return new Set(rows.flatMap((row, index) => (row.defaultChecked && !isReadOnlyRow(row) ? [rowIdFor(row, index)] : [])));
}

/**
 * The accept state behind the review step.
 *
 * The hook owns the ROWS as well as the acceptances, and they are only ever replaced together through
 * `loadDiff` / `loadDiffAfterRefusal`. That is not incidental tidiness — taking rows as a prop and
 * seeding off them is subtly wrong twice over: the wizard mounts this hook when the dialog OPENS, a
 * step before any diff exists, so a `useState` initializer seeds against an empty array and no
 * deterministic row ever starts accepted; and reconciling a later rows change with an effect makes the
 * refusal path a race between "seed the new defaults" and "clear everything". Two named loaders say
 * which of those two answers a caller wants, and neither can be reached by accident.
 *
 * Deliberately has **no "accept all"**: `setChecked` takes one field at a time and there is no bulk
 * setter for a caller to reach for. See `AirtableDiffGrid` for the full reasoning — in short, a
 * one-click accept turns a model's extraction into an approved config change without anyone reading the
 * source passage, which is the single failure this whole flow exists to prevent.
 *
 * Keyed off `rowIdFor`, never `fieldKey` — see the note on that function for why.
 */
export function useAirtableReview(initialRows: AirtableDiffRow[] = []): AirtableReviewState {
	const [rows, setRows] = useState<AirtableDiffRow[]>(initialRows);
	const [checkedKeys, setCheckedKeys] = useState<ReadonlySet<string>>(() => defaultCheckedKeys(initialRows));

	const entries = useMemo<AirtableReviewEntry[]>(
		() =>
			rows.map((row, index) => {
				const rowId = rowIdFor(row, index);
				const formKey = resolveFormKey(row.fieldKey);
				const unknownField = formKey === null;
				const acceptable = !isReadOnlyRow(row) && !unknownField;
				return {
					rowId,
					row,
					formKey,
					acceptable,
					checked: acceptable && checkedKeys.has(rowId),
					unknownField,
				};
			}),
		[rows, checkedKeys],
	);

	const countsByKind = useMemo(() => {
		const counts: Record<AirtableRowKind, number> = { ...EMPTY_COUNTS };
		for (const { row } of entries) counts[row.kind] += 1;
		return counts;
	}, [entries]);

	const acceptableCount = entries.filter((entry) => entry.acceptable).length;

	const acceptedRows = useMemo<AcceptedRow[]>(
		() => entries.flatMap((entry) => (entry.checked && entry.formKey !== null ? [{ row: entry.row, formKey: entry.formKey }] : [])),
		[entries],
	);

	const applyDisabledReasons = useMemo<TranslationKeys[]>(() => {
		const reasons: TranslationKeys[] = [];
		// Mirrors the Save button's `saveDisabledReasons` contract: a list of i18n keys, surfaced in a
		// tooltip, so a dead Apply button always says why it is dead.
		if (acceptedRows.length === 0) reasons.push("department.config.airtable.apply.disabled.nothingAccepted" as TranslationKeys);
		return reasons;
	}, [acceptedRows]);

	const setChecked = useCallback((rowId: string, checked: boolean) => {
		setCheckedKeys((previous) => {
			const next = new Set(previous);
			if (checked) next.add(rowId);
			else next.delete(rowId);
			return next;
		});
	}, []);

	const loadDiff = useCallback((next: AirtableDiffRow[]) => {
		setRows(next);
		setCheckedKeys(defaultCheckedKeys(next));
	}, []);

	const loadDiffAfterRefusal = useCallback((next: AirtableDiffRow[]) => {
		setRows(next);
		setCheckedKeys(new Set());
	}, []);

	return {
		entries,
		countsByKind,
		acceptableCount,
		acceptedCount: acceptedRows.length,
		acceptedRows,
		applyDisabledReasons,
		rows,
		setChecked,
		loadDiff,
		loadDiffAfterRefusal,
	};
}
